import React from 'react';
import {
  StyleSheet,
  StatusBar,
  FlatList,
  StyleProp,
  ViewStyle
} from 'react-native';
import { Provider } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';

import { Logger } from 'aws-amplify';

import initial, { useLayout, Layout, mobile } from '../constants/layout';
import { useSettings } from '../constants/settings';
import Sequence, { setting as sequence, sorted } from '../constants/sequence';
import { hexagrams, Guà } from '../constants/hexagram';

import { View } from './themed-view';
import Hexagram from './hexagram';
import Dashboard from './dashboard';
import FilterDialog from './filter';
import SortDialog from './sort';
import { Settings } from '../models/settings';
import Filter, { empty, filtered } from '../constants/filter';
import { ReadingParamList } from '../types';
import Reading from './reading';

export interface Props {
  onFilter?: (filter?: Filter) => void;
  reading?: ReadingParamList;
}

interface RenderProps {
  /** The hexagram guà */
  item: Guà;
  /** The hexagram style */
  style?: StyleProp<ViewStyle>;
  /** Function to call in case of item pressed */
  onPress?: (index: number) => void;
  /** Function to call in case of item pressed for long */
  onLongPress?: (index: number) => void;
}

const logger = new Logger('book');

// @todo: You have a large list that is slow to update - make sure your renderItem function renders components
// that follow React performance best practices like PureComponent, shouldComponentUpdate, etc

const Item = ({ item, style, onPress, onLongPress }: RenderProps) => (
  <Hexagram
    number={item.number}
    key={item.number.toString()}
    style={style}
    onPress={onPress}
    onLongPress={onLongPress}
  />
  // <View style={style}><Text>{item.number}.kkza</Text></View>
);

Item.defaultProps = {
  style: {},
  onPress: (index: number) => {
    logger.debug(`hexagram ${index} pressed`);
  },
  onLongPress: (index: number) => {
    logger.debug(`hexagram ${index} pressed for long`);
  }
};

export const styles = StyleSheet.create({
  mobile: {
    marginTop: StatusBar.currentHeight || 0
  },
  listContainer: {
    // paddingBottom: 60 // @todo depending on Appbar
    paddingBottom: 32
    // @todo calc from bottom-tab-navigation
  },
  list: {},
  top: {
    // position: 'absolute',
    // left: 0,
    // right: 0,
    // top: 0,
  },
  searchBar: {
    // maxWidth: '92%'
  },
  column: {}
});

const mobileRender = (content: JSX.Element | (JSX.Element | undefined)[]) =>
  mobile ? (
    <View style={mobile ? styles.mobile : undefined}>{content}</View>
  ) : (
    content
  );

const Book = (props: Props): JSX.Element => {
  const bw = (w: number, c: number, mh: number) => w / c - 2 * mh;
  const marginHorizontal = 8;
  const marginVertical = 4;

  const { onFilter, reading } = props;

  const [settings, setSettings] = React.useState<Settings>({});
  const [filter, setFilter] = React.useState<Filter>(empty()); // { type: "Lines", lines: [8, 7, 8, undefined, undefined, undefined]}
  const [filtering, setFiltering] = React.useState(false);
  const [sorting, setSorting] = React.useState(false);
  const [layout, setLayout] = React.useState<Layout>(initial);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [current, setCurrent] = React.useState<number | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchQuery, setSearchQuery] = React.useState('perseverancia');

  useLayout(layout, setLayout);
  const navigation = useNavigation();

  const { updateSetting } = useSettings(settings, setSettings);

  const showFilter = () => {
    setFiltering(true);
    logger.debug('filter shown');
  };

  const setFiltered = (f: Filter) => {
    setFilter(f);
    if (onFilter) onFilter(f);
  };

  const hideFilter = () => setFiltering(false);
  const removeFilter = () => setFiltered(empty());

  const showSort = () => {
    setSorting(true);
    logger.debug('Sort modal shown');
  };
  const hideSort = () => setSorting(false);
  const setSort = (value: Sequence) => updateSetting(sequence, value);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onChangeSearch = (query: React.SetStateAction<string>) =>
    setSearchQuery(query);

  const onPress = (index: number) => {
    logger.log(`hexagram ${index} pressed`);
    navigation.navigate('Exploring', {
      reading: { lines: hexagrams[index - 1].lines }
    });
  };

  const onLongPress = (index: number) => {
    setCurrent(index);
    logger.log(`hexagram ${index} pressed for long`);
  };

  const renderList = () =>
    mobileRender(
      <FlatList
        key={layout.window.width}
        data={sorted(filtered(hexagrams, filter), settings[sequence])}
        renderItem={({ item }) =>
          Item({
            item,
            style: {
              width: bw(layout.window.width, layout.columns, marginHorizontal),
              marginHorizontal,
              marginVertical
            },
            onPress,
            onLongPress
          })
        }
        numColumns={layout.columns}
        keyExtractor={(item) => item.number.toString()}
        contentContainerStyle={styles.listContainer}
        style={styles.list}
        columnWrapperStyle={layout.columns > 1 ? styles.column : undefined}
      />
    );

  return (
    <Provider>
      <FilterDialog
        key="filter"
        visible={filtering}
        onDismiss={hideFilter}
        value={filter}
        setValue={setFiltered}
        sequence={settings[sequence] as Sequence}
      />
      <SortDialog
        key="sort"
        visible={sorting}
        onDismiss={hideSort}
        value={settings[sequence] as Sequence}
        setValue={setSort}
      />
      {
        reading && reading.lines ? (
          <Reading key="reading" lines={reading.lines} />
        ) : (
          renderList()
        )
        // @todo Smoother transition between reading and rendering list
      }
      {!reading ? (
        <Dashboard
          key="dashboard"
          filter={filter}
          sort={settings[sequence] as Sequence}
          onFilter={showFilter}
          onRemoveFilter={removeFilter}
          onSort={showSort}
        />
      ) : undefined}
    </Provider>
  );

  // return (
  //   <View style={styles.container}>
  //     <Filter visible={filtering} onDismiss={hideFilter}/>
  //     <Appbar style={styles.top}>
  //       <Appbar.Action
  //         style={filtering ? styles.selectedActionIcon : styles.actionIcon}
  //         icon={({ color }: any) => <ActionIcon name="filter-menu-outline" color={color}/>}
  //         onPress={showFilter}
  //       />
  //       {/* @todo filter-menu when a filter is applied */}
  //       <Appbar.Action icon={({ color }: any) => <ActionIcon name="sort-bool-ascending" color={color} />} onPress={showFilter} />
  //       <Searchbar
  //           placeholder="Search"
  //           onChangeText={onChangeSearch}
  //           value={searchQuery}
  //           style={styles.searchBar}
  //       />
  //       {/* @todo Searchbar fade to top: https://github.com/jvidalv/astrale/blob/master/src/components/animations/FadeToTop.js */}
  //     </Appbar>
  //   </View>
  // );
};

export default Book;
